import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Plus, Search, Star, Phone, Mail, MapPin,
  Calendar, X, Clock, UserPlus, Trash2, Edit, Zap
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const statusLabels = {
  to_contact: { label: 'À contacter', color: 'bg-gray-100 text-gray-700' },
  interview_scheduled: { label: 'Entretien planifié', color: 'bg-blue-100 text-blue-700' },
  interview_done: { label: 'Entretien passé', color: 'bg-purple-100 text-purple-700' },
  retained: { label: 'Retenu', color: 'bg-amber-100 text-amber-700' },
  hired: { label: 'Embauché', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700' }
};

const availabilityLabels = {
  immediate: { label: 'Immédiate', color: 'bg-green-100 text-green-700' },
  one_month: { label: 'Sous 1 mois', color: 'bg-blue-100 text-blue-700' },
  three_months: { label: 'Sous 3 mois', color: 'bg-amber-100 text-amber-700' },
  not_available: { label: 'Non disponible', color: 'bg-red-100 text-red-700' }
};

const roleLabels = {
  administrator: { label: 'Administrateur', color: 'bg-purple-100 text-purple-700' },
  hr_management: { label: 'Gestion RH', color: 'bg-pink-100 text-pink-700' },
  marketing: { label: 'Marketing', color: 'bg-cyan-100 text-cyan-700' },
  assistant: { label: 'Assistant', color: 'bg-gray-100 text-gray-700' }
};

const skillOptions = [
  'prospection', 'customer_service', 'administrative', 'accounting',
  'social_media', 'content_writing', 'data_entry', 'other'
];

const skillLabels = {
  prospection: 'Prospection',
  customer_service: 'Service client',
  administrative: 'Administratif',
  accounting: 'Comptabilité',
  social_media: 'Réseaux sociaux',
  content_writing: 'Rédaction',
  data_entry: 'Saisie données',
  other: 'Autre'
};

// Format salary in Ariary
const formatAriary = (amount) => {
  if (!amount) return '-';
  return new Intl.NumberFormat('fr-MG').format(amount) + ' Ar';
};

export default function AdminCandidatesPage() {
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position_applied: 'Assistant virtuel',
    role: 'assistant',
    source: '',
    location: 'Madagascar',
    salary_requested: '',
    availability: 'immediate',
    can_join_quickly: false,
    skills: [],
    languages: ['Français'],
    notes: ''
  });

  useEffect(() => {
    fetchCandidates();
    fetchStats();
  }, [statusFilter]);

  const fetchCandidates = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      let url = `${API_URL}/api/admin-crm/candidates`;
      if (statusFilter) url += `?status=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCandidates(data);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Erreur lors du chargement des candidats');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/candidates/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    
    try {
      const url = selectedCandidate 
        ? `${API_URL}/api/admin-crm/candidates/${selectedCandidate.id}` 
        : `${API_URL}/api/admin-crm/candidates`;
      
      const method = selectedCandidate ? 'PUT' : 'POST';
      
      let body;
      if (selectedCandidate) {
        const params = new URLSearchParams();
        Object.entries(formData).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            if (key === 'skills' || key === 'languages') {
              params.append(key, Array.isArray(value) ? value.join(',') : value);
            } else if (key === 'can_join_quickly') {
              params.append(key, value ? 'true' : 'false');
            } else {
              params.append(key, value);
            }
          }
        });
        body = params;
      } else {
        body = JSON.stringify({
          ...formData,
          salary_requested: formData.salary_requested ? parseFloat(formData.salary_requested) : null
        });
      }
      
      const response = await fetch(url, {
        method,
        headers: selectedCandidate 
          ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/x-www-form-urlencoded' }
          : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body
      });

      if (response.ok) {
        toast.success(selectedCandidate ? 'Candidat mis à jour' : 'Candidat créé');
        setShowModal(false);
        resetForm();
        fetchCandidates();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving candidate:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleHire = async (candidate) => {
    if (!confirm(`Veux-tu embaucher ${candidate.first_name} ${candidate.last_name} et l'ajouter aux assistants ?`)) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/candidates/${candidate.id}/hire?hourly_rate=0`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`${candidate.first_name} a été embauché et ajouté aux assistants !`);
        fetchCandidates();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'embauche');
      }
    } catch (error) {
      console.error('Error hiring candidate:', error);
      toast.error('Erreur lors de l\'embauche');
    }
  };

  const handleDelete = async (candidate) => {
    if (!confirm(`Supprimer ${candidate.first_name} ${candidate.last_name} ?`)) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/candidates/${candidate.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Candidat supprimé');
        fetchCandidates();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting candidate:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStatusChange = async (candidate, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      const params = new URLSearchParams();
      params.append('status', newStatus);
      
      const response = await fetch(`${API_URL}/api/admin-crm/candidates/${candidate.id}?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchCandidates();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position_applied: 'Assistant virtuel',
      role: 'assistant',
      source: '',
      location: 'Madagascar',
      salary_requested: '',
      availability: 'immediate',
      can_join_quickly: false,
      skills: [],
      languages: ['Français'],
      notes: ''
    });
    setSelectedCandidate(null);
  };

  const openEditModal = (candidate) => {
    setSelectedCandidate(candidate);
    setFormData({
      first_name: candidate.first_name || '',
      last_name: candidate.last_name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      position_applied: candidate.position_applied || 'Assistant virtuel',
      role: candidate.role || 'assistant',
      source: candidate.source || '',
      location: candidate.location || 'Madagascar',
      salary_requested: candidate.salary_requested || '',
      availability: candidate.availability || 'immediate',
      can_join_quickly: candidate.can_join_quickly || false,
      skills: candidate.skills || [],
      languages: candidate.languages || ['Français'],
      notes: candidate.notes || ''
    });
    setShowModal(true);
  };

  const filteredCandidates = candidates.filter(c => 
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-3 h-3 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} 
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-candidates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Espace RH - Candidats</h1>
          <p className="text-gray-500">Gère tes candidats et recrutements</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-candidate-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau candidat
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { key: 'total', label: 'Total', color: 'bg-gray-100 text-gray-700' },
          { key: 'to_contact', label: 'À contacter', color: 'bg-gray-100 text-gray-700' },
          { key: 'interview_scheduled', label: 'Entretiens', color: 'bg-blue-100 text-blue-700' },
          { key: 'interview_done', label: 'Passés', color: 'bg-purple-100 text-purple-700' },
          { key: 'retained', label: 'Retenus', color: 'bg-amber-100 text-amber-700' },
          { key: 'hired', label: 'Embauchés', color: 'bg-green-100 text-green-700' },
          { key: 'rejected', label: 'Refusés', color: 'bg-red-100 text-red-700' }
        ].map(stat => (
          <motion.div 
            key={stat.key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${stat.color} rounded-lg p-3 text-center cursor-pointer hover:opacity-80 transition-opacity`}
            onClick={() => setStatusFilter(stat.key === 'total' ? '' : stat.key)}
          >
            <p className="text-2xl font-bold">{stats?.[stat.key] || 0}</p>
            <p className="text-xs">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un candidat..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="candidate-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="candidate-status-filter"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(statusLabels).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Candidates List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {filteredCandidates.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun candidat trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-600">Candidat</th>
                  <th className="text-left p-4 font-medium text-gray-600">Rôle</th>
                  <th className="text-left p-4 font-medium text-gray-600">Localisation</th>
                  <th className="text-left p-4 font-medium text-gray-600">Salaire (Ar)</th>
                  <th className="text-left p-4 font-medium text-gray-600">Disponibilité</th>
                  <th className="text-left p-4 font-medium text-gray-600">Statut</th>
                  <th className="text-right p-4 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCandidates.map((candidate) => (
                  <motion.tr 
                    key={candidate.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                    data-testid={`candidate-row-${candidate.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center relative">
                          <span className="text-blue-600 font-semibold">
                            {candidate.first_name?.charAt(0)}{candidate.last_name?.charAt(0)}
                          </span>
                          {candidate.can_join_quickly && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center" title="Peut rejoindre rapidement">
                              <Zap className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {candidate.first_name} {candidate.last_name}
                          </p>
                          <p className="text-sm text-gray-500">{candidate.email || 'Pas d\'email'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${roleLabels[candidate.role]?.color || 'bg-gray-100'}`}>
                        {roleLabels[candidate.role]?.label || candidate.position_applied}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 text-gray-600">
                        <MapPin className="w-3 h-3" />
                        {candidate.location}
                      </div>
                    </td>
                    <td className="p-4 text-gray-600 font-medium">
                      {formatAriary(candidate.salary_requested)}
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${availabilityLabels[candidate.availability]?.color || 'bg-gray-100'}`}>
                        {availabilityLabels[candidate.availability]?.label || 'N/A'}
                      </span>
                    </td>
                    <td className="p-4">
                      <select
                        value={candidate.status}
                        onChange={(e) => handleStatusChange(candidate, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${statusLabels[candidate.status]?.color || 'bg-gray-100'}`}
                      >
                        {Object.entries(statusLabels).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {candidate.status === 'retained' && (
                          <Button
                            size="sm"
                            onClick={() => handleHire(candidate)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`hire-btn-${candidate.id}`}
                          >
                            <UserPlus className="w-3 h-3 mr-1" />
                            Embaucher
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(candidate)}
                          data-testid={`edit-btn-${candidate.id}`}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(candidate)}
                          data-testid={`delete-btn-${candidate.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="candidate-modal"
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedCandidate ? 'Modifier le candidat' : 'Nouveau candidat'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-last-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Poste visé *</label>
                  <input
                    type="text"
                    required
                    value={formData.position_applied}
                    onChange={(e) => setFormData({...formData, position_applied: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-position"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-role"
                  >
                    {Object.entries(roleLabels).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <input
                    type="text"
                    placeholder="LinkedIn, Indeed, Recommandation..."
                    value={formData.source}
                    onChange={(e) => setFormData({...formData, source: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-source"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-location"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salaire demandé (Ariary/mois)</label>
                  <input
                    type="number"
                    value={formData.salary_requested}
                    onChange={(e) => setFormData({...formData, salary_requested: e.target.value})}
                    placeholder="Ex: 2000000"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-salary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Disponibilité</label>
                  <select
                    value={formData.availability}
                    onChange={(e) => setFormData({...formData, availability: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="candidate-availability"
                  >
                    {Object.entries(availabilityLabels).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <input
                  type="checkbox"
                  id="can_join_quickly"
                  checked={formData.can_join_quickly}
                  onChange={(e) => setFormData({...formData, can_join_quickly: e.target.checked})}
                  className="w-5 h-5 text-green-600 rounded"
                  data-testid="candidate-can-join-quickly"
                />
                <label htmlFor="can_join_quickly" className="flex items-center gap-2 cursor-pointer">
                  <Zap className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Peut nous rejoindre rapidement</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Compétences</label>
                <div className="flex flex-wrap gap-2">
                  {skillOptions.map(skill => (
                    <label key={skill} className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.skills.includes(skill)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, skills: [...formData.skills, skill]});
                          } else {
                            setFormData({...formData, skills: formData.skills.filter(s => s !== skill)});
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-600">{skillLabels[skill]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Notes sur le candidat..."
                  data-testid="candidate-notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-candidate-btn">
                  {selectedCandidate ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
